/**
 * PlantingCardInline — sub-formulario inline para editar una planting
 * ADICIONAL (ordering >= 2) dentro del propio form de la parcela.
 *
 * Réplica funcional del `PlantingCard` de la app móvil
 * (mobile/src/screens/auditDetail/plotWizard/PlantingCard.tsx). La
 * plantación principal (ordering=1) NO se pinta aquí: sus datos son
 * los del padre (nombre, marco, superficie, variedad…) y se propagan
 * via `syncPrimaryFromParent` al guardar la parcela.
 *
 * Reglas visibles:
 *   · Marco NO se pide por planting — vive en el padre.
 *   · Superficie se sugiere:
 *       - Doblado TOTAL (sin sub_geom) → superficie del padre.
 *       - Doblado PARCIAL (con sub_geom) → área del sub-polígono.
 *   · Nº plantas se sugiere = superficie efectiva × densidad del
 *     padre (marco_row × marco_col). En modo total el padre ya envía
 *     `parentPlants = total / N` para no doblar el conteo.
 *   · Botón "Dibujar sub-área" sólo en modo parcial.
 */

import { useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import type { AuditPlotPlanting } from "@/types/audit";
import { computeGeodesicAreaHa } from "@/utils/geo";

const { Text } = Typography;

interface CatalogueOption {
  id: number;
  name: string;
  code?: string;
}

interface Props {
  planting: AuditPlotPlanting;
  varieties: CatalogueOption[];
  patterns: CatalogueOption[];
  plotTypes: CatalogueOption[];
  intermediates: CatalogueOption[];
  /** Geometría del padre — sirve para sugerir superficie en doblado
   *  total (sin sub_geom propia). */
  parentGeom: GeoJSON.Geometry | null;
  /** Marco de la parcela padre — se usa para sugerir Nº plantas de
   *  la planting extra. Vive siempre en el padre; nunca se pide por
   *  planting (misma decisión que en móvil). */
  parentMarcoRow: number | null;
  parentMarcoCol: number | null;
  /** Plantas totales del padre (o autoPlants calculado en el padre).
   *  En doblado total, la sugerencia por planting es este valor
   *  dividido entre `totalPlantings` (el padre YA envía total/N si
   *  aplica el reparto). */
  parentPlants: number | null;
  /** Nº total de plantings de la parcela (principal + extras). */
  totalPlantings: number;
  /** Modo del doblado a nivel parcela. Cambia comportamiento del
   *  botón "Dibujar sub-área" y de las sugerencias. */
  isPartialMode: boolean;
  /** Callback para abrir el editor de sub-geometría (Fase C). Sólo se
   *  cablea en modo parcial; en total se muestra la superficie del
   *  padre y no hay dibujo dedicado. */
  onDrawSubGeom?: () => void;
  onUpdate: (patch: Partial<AuditPlotPlanting>) => Promise<AuditPlotPlanting>;
  onRemove: () => Promise<void>;
}

const accentInsensitiveFilter = (
  input: string,
  option?: { label?: unknown },
) => {
  const norm = (s: unknown) =>
    String(s ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");
  return norm(option?.label).includes(norm(input));
};

export const PlantingCardInline: React.FC<Props> = ({
  planting,
  varieties,
  patterns,
  plotTypes,
  intermediates,
  parentGeom,
  parentMarcoRow,
  parentMarcoCol,
  parentPlants,
  totalPlantings,
  isPartialMode,
  onDrawSubGeom,
  onUpdate,
  onRemove,
}) => {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Sync el form con la prop cada vez que cambia la planting (p.ej.
  // tras refresh externo o cambio de modo).
  useEffect(() => {
    form.setFieldsValue({
      variety: planting.variety,
      pattern: planting.pattern,
      plot_type: planting.plot_type,
      intermediate_wood: planting.intermediate_wood,
      crop_date: planting.crop_date,
      plants: planting.plants,
      grafting: planting.grafting,
      surface: planting.surface,
    });
  }, [planting, form]);

  const isInjerta = useMemo(() => {
    const name =
      plotTypes.find((p) => p.id === planting.plot_type)?.name ?? "";
    return /injerta|injerto/i.test(name);
  }, [plotTypes, planting.plot_type]);

  const hasSubGeom = planting.sub_geom != null;

  // Superficie sugerida: en parcial, del propio sub_geom; en total,
  // la del padre. Sin geometría → null (no se ofrece sugerencia).
  const suggestedSurfaceHa = useMemo(() => {
    if (hasSubGeom && planting.sub_geom) {
      return computeGeodesicAreaHa(planting.sub_geom);
    }
    return computeGeodesicAreaHa(parentGeom);
  }, [hasSubGeom, planting.sub_geom, parentGeom]);

  // Plantas sugeridas — usa marco del padre + superficie efectiva.
  //   · Parcial (sub_geom): sub_area × densidad(padre).
  //   · Total (sin sub_geom): parentPlants tal cual (el caller ya
  //     divide total/N para no duplicar el conteo).
  const suggestedPlants = useMemo(() => {
    if (isPartialMode && hasSubGeom && suggestedSurfaceHa != null) {
      if (
        parentMarcoRow != null &&
        parentMarcoRow > 0 &&
        parentMarcoCol != null &&
        parentMarcoCol > 0
      ) {
        const m2 = suggestedSurfaceHa * 10_000;
        return Math.round(m2 / (parentMarcoRow * parentMarcoCol));
      }
      return null;
    }
    return parentPlants ?? null;
  }, [
    isPartialMode,
    hasSubGeom,
    suggestedSurfaceHa,
    parentMarcoRow,
    parentMarcoCol,
    parentPlants,
  ]);

  const commitPatch = async (patch: Partial<AuditPlotPlanting>) => {
    setSaving(true);
    try {
      await onUpdate(patch);
    } catch (err) {
      const detail =
        (err as { response?: { data?: unknown } }).response?.data ??
        (err as Error).message;
      message.error(`Error al guardar: ${JSON.stringify(detail)}`);
    } finally {
      setSaving(false);
    }
  };

  const applySurfaceSuggestion = () => {
    if (suggestedSurfaceHa == null) return;
    const v = Number(suggestedSurfaceHa.toFixed(4));
    form.setFieldValue("surface", v);
    void commitPatch({ surface: v });
  };

  const applyPlantsSuggestion = () => {
    if (suggestedPlants == null) return;
    form.setFieldValue("plants", suggestedPlants);
    void commitPatch({ plants: suggestedPlants });
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await onRemove();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? (err as Error).message;
      message.error(`No se pudo eliminar: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space>
          <Tag color="orange">Plantación #{planting.ordering}</Tag>
          {isPartialMode ? (
            hasSubGeom ? (
              <Tag color="green">Sub-área dibujada</Tag>
            ) : (
              <Tag color="volcano">Sin sub-área</Tag>
            )
          ) : null}
        </Space>
      }
      extra={
        <Space>
          {isPartialMode && onDrawSubGeom ? (
            <Tooltip
              title={
                hasSubGeom
                  ? "Editar sub-área dibujada"
                  : "Dibujar sub-área dentro de la parcela"
              }
            >
              <Button
                size="small"
                icon={
                  hasSubGeom ? <EditOutlined /> : <EnvironmentOutlined />
                }
                onClick={onDrawSubGeom}
              >
                {hasSubGeom ? "Editar sub-área" : "Dibujar sub-área"}
              </Button>
            </Tooltip>
          ) : null}
          <Popconfirm
            title="¿Eliminar esta plantación?"
            description="Esta acción no se puede deshacer."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={handleRemove}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={saving}
            />
          </Popconfirm>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changed) => {
          // Persistimos on-blur / on-select; onValuesChange incluye
          // teclado en tiempo real. Usamos un mini-debounce implícito:
          // sólo persistimos cuando el campo tiene un valor "final"
          // (para InputNumber onChange dispara con cada tecla). Aquí
          // nos limitamos a persistir cambios de Select y de valores
          // que terminan siendo el mismo tipo esperado por el back.
          const key = Object.keys(changed)[0];
          if (!key) return;
          // Los Select disparan cambios inmediatos y podemos persistir
          // en el momento. Los InputNumber los persistimos on-blur
          // via el handler propio del componente para no spamear
          // requests con cada tecla.
          if (
            [
              "variety",
              "pattern",
              "plot_type",
              "intermediate_wood",
            ].includes(key)
          ) {
            void commitPatch({ [key]: changed[key] } as Partial<AuditPlotPlanting>);
          }
        }}
      >
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              name="variety"
              label="Variedad"
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                filterOption={accentInsensitiveFilter}
                placeholder="Seleccionar"
                options={varieties.map((v) => ({
                  value: v.id,
                  label: v.code ? `${v.code} · ${v.name}` : v.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="pattern" label="Patrón">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={accentInsensitiveFilter}
                placeholder="Sin patrón"
                options={patterns.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="plot_type" label="Tipo">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={accentInsensitiveFilter}
                placeholder="Sin tipo"
                options={plotTypes.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={8}>
            <Form.Item
              name="crop_date"
              label="Año plantación"
              rules={[
                {
                  type: "number",
                  min: 1900,
                  max: new Date().getFullYear() + 5,
                },
              ]}
            >
              <InputNumber
                min={1900}
                max={new Date().getFullYear() + 5}
                style={{ width: "100%" }}
                placeholder="p.ej. 2020"
                onBlur={() =>
                  commitPatch({ crop_date: form.getFieldValue("crop_date") })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="Superficie (ha)"
              name="surface"
              extra={
                suggestedSurfaceHa != null &&
                Math.abs((form.getFieldValue("surface") ?? 0) -
                  suggestedSurfaceHa) > 0.005 ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: "auto" }}
                    onClick={applySurfaceSuggestion}
                  >
                    Usar {suggestedSurfaceHa.toFixed(4)} ha
                  </Button>
                ) : null
              }
            >
              <InputNumber
                min={0}
                step={0.0001}
                precision={4}
                style={{ width: "100%" }}
                addonAfter="ha"
                onBlur={() =>
                  commitPatch({ surface: form.getFieldValue("surface") })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="Nº plantas"
              name="plants"
              extra={
                suggestedPlants != null &&
                form.getFieldValue("plants") !== suggestedPlants ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: "auto" }}
                    onClick={applyPlantsSuggestion}
                  >
                    Usar {suggestedPlants}
                  </Button>
                ) : null
              }
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                onBlur={() =>
                  commitPatch({ plants: form.getFieldValue("plants") })
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {isInjerta ? (
          <Row gutter={12}>
            <Col xs={16}>
              <Form.Item name="intermediate_wood" label="Madera intermedia">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={accentInsensitiveFilter}
                  placeholder="Sin madera intermedia"
                  options={intermediates.map((i) => ({
                    value: i.id,
                    label: i.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="grafting" label="Nº injertos">
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  onBlur={() =>
                    commitPatch({ grafting: form.getFieldValue("grafting") })
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        ) : null}

        {totalPlantings <= 1 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Añade la plantación principal desde los campos del padre —
            esta card sólo aparece cuando hay al menos 2 plantaciones.
          </Text>
        ) : null}
      </Form>
    </Card>
  );
};
